/**
 * Dental Sale mega menu — behavior for desktop + mobile.
 *
 * ## Liquid / DOM structure (see `blocks/_header-menu.liquid`, `snippets/header-drawer.liquid`)
 * - Panel blocks (`dental-mega-categories`, `dental-mega-image-grid`, `dental-mega-columns`,
 *   `dental-mega-brands`, `dental-mega-sale`) are rendered via `dental-mega-panels-markup`.
 * - **Desktop:** that markup is injected inside `<dental-sale-mega-menu>` → `[data-dental-mega-menu]`. Tabs live in
 *   `nav.dental-mega-nav__bar`; each tab’s `data-panel` matches a `.dental-mega-panel`’s `data-panel` (block id; may
 *   differ by suffix between duplicated DOM trees — we normalize trailing `-<digits>` when matching).
 * - **Mobile drawer:** classic level-0 list under `[data-drawer-mega]`; each top item drills into its
 *   `dental-mega-panel` via Horizon `details` / `menu-drawer__submenu` (back = `header-drawer/back`).
 *   No Swiper tab strip. Panel layouts (featured / brands / sale / …) stay in the panel body.
 *   Drawer L0 sale styling is Liquid-driven; desktop tab chrome still synced by helpers below.
 *
 * ## This file’s responsibilities
 * 1. **Tab chrome sync** — `syncDentalMegaTabChrome(scope)`: reads each panel’s `data-tab-sale`, `data-show-chevron`,
 *    etc., and toggles `dental-mega-nav__item--sale` / `dental-mega-nav__item--has-chev` (+ chevron span) on tabs.
 *    Tabs also carry `data-sync-sale` / `data-sync-chev` from Liquid so editor re-renders that strip panel `data-*`
 *    cannot clear sale styling or force a chevron when the block has it turned off.
 * 2. **`DentalSaleMegaMenu` custom element** — desktop bar + dropdown: open panel on hover/click, close on leave
 *    (debounced), backdrop click, Escape; optional sticky nav height; link mobile menu `<details>` to full-width overlay.
 *
 * ## CSS
 * `assets/dental-sale-mega-menu.css` — layout, open states, drawer mega layout, sale tab visuals.
 */
(() => {
/**
 * Match tab `data-panel` to panel when Shopify/editor adds different numeric suffixes to the same block instance.
 * @param {string | null | undefined} key
 */
function normalizeDentalMegaPanelKey(key) {
  if (!key) return '';
  return key.replace(/-\d+$/, '');
}

/** @param {string | null | undefined} rawValue @param {boolean} fallback */
function isDentalMegaTruthy(rawValue, fallback = false) {
  if (rawValue == null || rawValue === '' || rawValue === 'null') return fallback;
  const s = String(rawValue).trim().toLowerCase();
  if (s === 'false' || s === '0' || s === 'no') return false;
  return s === 'true' || s === '1' || s === 'yes';
}

/** @param {HTMLElement} el @param {string} kebab */
function readDentalMegaDataAttr(el, kebab) {
  const camel = kebab.replace(/-([a-z])/g, (_, c) => c.toUpperCase());
  const fromDataset = /** @type {Record<string, string | undefined>} */ (el.dataset)[camel];
  if (fromDataset != null && fromDataset !== '') return fromDataset;
  return el.getAttribute(`data-${kebab}`);
}

/**
 * @param {Map<string, HTMLElement[]>} map
 * @param {string | null | undefined} key
 * @param {HTMLElement} panel
 */
function indexDentalMegaPanel(map, key, panel) {
  const normalized = normalizeDentalMegaPanelKey(key);
  if (!normalized) return;
  if (!map.has(normalized)) map.set(normalized, []);
  const list = map.get(normalized);
  if (list && !list.includes(panel)) list.push(panel);
}

/**
 * Resolve panels for a tab via data-panel and/or data-panel-handle.
 * @param {HTMLElement} tab
 * @param {Map<string, HTMLElement[]>} panelsByKey
 * @returns {HTMLElement[]}
 */
function findDentalMegaPanelsForTab(tab, panelsByKey) {
  /** @type {HTMLElement[]} */
  const found = [];
  const keys = [tab.dataset.panel, tab.dataset.panelHandle, readDentalMegaDataAttr(tab, 'panel-handle')];
  for (const key of keys) {
    const normalized = normalizeDentalMegaPanelKey(key);
    if (!normalized) continue;
    for (const panel of panelsByKey.get(normalized) || []) {
      if (!found.includes(panel)) found.push(panel);
    }
  }
  return found;
}

/**
 * Rough Shopify-like handle from a visible label (for sale-tab matching fallback).
 * @param {string | null | undefined} value
 */
function roughDentalMegaHandle(value) {
  return String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim()
    .replace(/ä/g, 'ae')
    .replace(/ö/g, 'oe')
    .replace(/ü/g, 'ue')
    .replace(/ß/g, 'ss')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

/**
 * Whether a mega panel should drive sale (pink) tab chrome.
 * @param {HTMLElement} panel
 */
function isDentalMegaSalePanel(panel) {
  const layoutStyle = (readDentalMegaDataAttr(panel, 'layout-style') || '').toLowerCase();
  if (layoutStyle === 'sale') return true;
  const tabSaleRaw = readDentalMegaDataAttr(panel, 'tab-sale');
  if (tabSaleRaw != null && tabSaleRaw !== '') return isDentalMegaTruthy(tabSaleRaw, false);
  return isDentalMegaTruthy(readDentalMegaDataAttr(panel, 'emphasize-sale'), false);
}

/**
 * True when a tab href points at a sale panel's collection/handle.
 * @param {HTMLElement} tab
 * @param {Set<string>} saleHandles
 */
function dentalMegaTabHrefMatchesSale(tab, saleHandles) {
  if (!saleHandles.size) return false;
  const href = (tab.getAttribute('href') || '').toLowerCase();
  if (!href) return false;
  for (const handle of saleHandles) {
    if (!handle) continue;
    if (href.includes(`/collections/${handle}`)) return true;
    if (href.includes(`/${handle}`) && (href.endsWith(`/${handle}`) || href.includes(`/${handle}?`))) return true;
  }
  return false;
}

/**
 * Apply panel-driven chevron + sale styling to mega tabs under `scope`
 * (desktop `<dental-sale-mega-menu>` or mobile `[data-drawer-mega]`).
 *
 * Sale chrome is additive: once a panel/handle/href says sale, we set class +
 * `data-sync-sale="1"` and never clear it. Header section hydration morphs the
 * header after idle and can wipe Liquid's `--sale` class; timeouts alone race that morph.
 *
 * @param {HTMLElement} scope
 */
function syncDentalMegaTabChrome(scope) {
  /** @type {Map<string, HTMLElement[]>} */
  const panelsByKey = new Map();
  /** @type {Set<string>} */
  const saleHandles = new Set();

  for (const panel of scope.querySelectorAll('.dental-mega-panel')) {
    if (!(panel instanceof HTMLElement)) continue;
    indexDentalMegaPanel(panelsByKey, panel.dataset.panel, panel);
    indexDentalMegaPanel(panelsByKey, panel.dataset.panelHandle, panel);
    indexDentalMegaPanel(panelsByKey, readDentalMegaDataAttr(panel, 'panel-handle'), panel);

    if (!isDentalMegaSalePanel(panel)) continue;
    for (const key of [
      panel.dataset.panel,
      panel.dataset.panelHandle,
      readDentalMegaDataAttr(panel, 'panel-handle'),
    ]) {
      const normalized = normalizeDentalMegaPanelKey(key);
      if (normalized) saleHandles.add(normalized);
    }
  }

  for (const tab of scope.querySelectorAll('[data-dental-mega-tab]')) {
    if (!(tab instanceof HTMLElement)) continue;
    const panelKey = normalizeDentalMegaPanelKey(tab.dataset.panel || tab.dataset.panelHandle);
    if (!panelKey && !tab.dataset.syncSale) continue;

    const panels = findDentalMegaPanelsForTab(tab, panelsByKey);
    const tabSaleFallback = readDentalMegaDataAttr(tab, 'sync-sale');
    const tabChevFallback = readDentalMegaDataAttr(tab, 'sync-chev');

    let panelSale = false;
    let panelChev = true;
    let sawPanelChev = false;

    if (panels.length) {
      for (const panel of panels) {
        if (isDentalMegaSalePanel(panel)) panelSale = true;

        const showChevronRaw = readDentalMegaDataAttr(panel, 'show-chevron');
        if (showChevronRaw != null && showChevronRaw !== '') {
          sawPanelChev = true;
          panelChev = panelChev && isDentalMegaTruthy(showChevronRaw, true);
        }
      }
    }

    const tabKeys = [
      tab.dataset.panel,
      tab.dataset.panelHandle,
      readDentalMegaDataAttr(tab, 'panel-handle'),
      roughDentalMegaHandle(tab.querySelector('.dental-mega-nav__item-label')?.textContent),
    ]
      .map((key) => normalizeDentalMegaPanelKey(key))
      .filter(Boolean);

    const saleByHandle = tabKeys.some((key) => saleHandles.has(key));
    const saleByHref = dentalMegaTabHrefMatchesSale(tab, saleHandles);
    // Panel / handle / href win over a stale data-sync-sale="0" after section hydration morphs.
    const emphasizeSale =
      panelSale ||
      saleByHandle ||
      saleByHref ||
      isDentalMegaTruthy(tabSaleFallback, false) ||
      tab.classList.contains('dental-mega-nav__item--sale');

    const showChevron =
      tabChevFallback != null && tabChevFallback !== ''
        ? isDentalMegaTruthy(tabChevFallback, true)
        : sawPanelChev
          ? panelChev
          : true;

    tab.classList.toggle('dental-mega-nav__item--has-chev', showChevron);

    if (emphasizeSale) {
      tab.classList.add('dental-mega-nav__item--sale');
      if (tab.getAttribute('data-sync-sale') !== '1') {
        tab.setAttribute('data-sync-sale', '1');
      }
    }
    // Never strip --sale here: hydration morph + a failed panel match was clearing Aktion pink.

    const existingChev = tab.querySelector('.dental-mega-nav__chev');
    if (showChevron && !existingChev) {
      const chev = document.createElement('span');
      chev.className = 'dental-mega-nav__chev';
      chev.setAttribute('aria-hidden', 'true');
      chev.textContent = '▼';
      tab.appendChild(chev);
    } else if (!showChevron && existingChev) {
      existingChev.remove();
    }
  }
}

function initDrawerMegaTabChrome() {
  // Each mobile drawer instance: tabs + `.dental-mega-panel` share one root (`data-drawer-mega`).
  document.querySelectorAll('[data-drawer-mega]').forEach((root) => {
    if (root instanceof HTMLElement) syncDentalMegaTabChrome(root);
  });
}

function initDesktopMegaTabChrome() {
  document.querySelectorAll('dental-sale-mega-menu').forEach((root) => {
    if (root instanceof HTMLElement) syncDentalMegaTabChrome(root);
  });
}

/** @type {AbortController | null} */
let dentalMegaDrawerToggleAbort = null;

/** Re-bind after `shopify:section:load` so we never stack listeners on the same `<details>`. */
function bindDentalMegaDrawerChromeResync() {
  const details = document.querySelector('#Details-menu-drawer-container');
  if (!(details instanceof HTMLDetailsElement)) return;
  dentalMegaDrawerToggleAbort?.abort();
  dentalMegaDrawerToggleAbort = new AbortController();
  const { signal } = dentalMegaDrawerToggleAbort;
  details.addEventListener(
    'toggle',
    () => {
      if (!details.open) return;
      requestAnimationFrame(() => {
        initDrawerMegaTabChrome();
        requestAnimationFrame(() => initDrawerMegaTabChrome());
      });
    },
    { signal }
  );
}

/** Refresh tab chrome for every desktop mega instance and every drawer mega root. */
function resyncAllDentalMegaTabChrome() {
  initDesktopMegaTabChrome();
  initDrawerMegaTabChrome();
}

/** @type {MutationObserver | null} */
let dentalMegaChromeObserver = null;
/** @type {number} */
let dentalMegaChromeObserverTimer = 0;

/**
 * Re-apply sale/chevron chrome whenever header hydration (or anything else) mutates
 * tab classes / data attributes under the mega roots.
 */
function bindDentalMegaChromeMutationObserver() {
  if (dentalMegaChromeObserver || typeof MutationObserver === 'undefined') return;

  dentalMegaChromeObserver = new MutationObserver(() => {
    window.clearTimeout(dentalMegaChromeObserverTimer);
    dentalMegaChromeObserverTimer = window.setTimeout(() => {
      resyncAllDentalMegaTabChrome();
    }, 50);
  });

  const observeRoot = (root) => {
    if (!(root instanceof HTMLElement)) return;
    dentalMegaChromeObserver?.observe(root, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ['class', 'data-sync-sale', 'data-tab-sale', 'data-emphasize-sale', 'data-layout-style'],
    });
  };

  document.querySelectorAll('dental-sale-mega-menu, [data-drawer-mega]').forEach(observeRoot);

  // Header section wrapper — catch morphs that replace the custom element node itself.
  document.querySelectorAll('.header-section, [id^="shopify-section-"]').forEach((section) => {
    if (!(section instanceof HTMLElement)) return;
    if (!section.querySelector('dental-sale-mega-menu, [data-drawer-mega]')) return;
    dentalMegaChromeObserver?.observe(section, { childList: true, subtree: true });
  });
}

/**
 * Custom element wrapping **desktop** top bar + flyout (`.dental-mega-menu` + backdrop).
 * Mobile drawer mega uses `[data-drawer-mega]` + global listeners at the bottom of this file, not this class.
 */
class DentalSaleMegaMenu extends HTMLElement {
  #openTimer = 0;
  #closeTimer = 0;
  #stickyEnabled = false;
  /** @type {HTMLButtonElement | null} */
  #mobileToggle = null;
  /** @type {HTMLDetailsElement | null} */
  #headerDrawerDetails = null;
  /** @type {string | null} */
  #currentPanel = null;
  /** @type {HTMLElement | null} */
  #lastTrigger = null;
  /** @type {HTMLElement | null} */
  #hoverTab = null;
  #bound = false;
  #initAttempts = 0;

  /** @type {HTMLElement | null} */
  #menu = null;
  /** @type {HTMLElement | null} */
  #backdrop = null;
  /** @type {HTMLElement | null} */
  #nav = null;

  connectedCallback() {
    // Parser may call connectedCallback before children exist — defer init.
    this.#scheduleInit();
  }

  disconnectedCallback() {
    this.#teardown();
  }

  #scheduleInit() {
    queueMicrotask(() => this.#init());
    requestAnimationFrame(() => this.#init());
  }

  #init() {
    if (this.#bound) return;

    this.#menu = this.querySelector('[data-dental-mega-menu]');
    this.#backdrop = this.querySelector('[data-dental-mega-backdrop]');
    this.#nav = this.querySelector('nav.dental-mega-nav__bar');
    this.#mobileToggle = this.querySelector('[data-dental-mega-mobile-toggle]');
    this.#stickyEnabled = this.dataset.stickyEnabled === 'true';

    // Wait until the nav (and ideally tabs) are in the light DOM.
    if (!this.#nav || (!this.#getTabs().length && this.#initAttempts < 10)) {
      this.#initAttempts += 1;
      requestAnimationFrame(() => this.#init());
      return;
    }

    this.#bound = true;
    this.#stickyEnabled = this.dataset.stickyEnabled === 'true';

    this.addEventListener('keydown', this.#onKeydown);
    // Event delegation: survives late children / theme morphs
    this.addEventListener('mouseover', this.#onMouseOver);
    this.addEventListener('click', this.#onTabClick);
    document.addEventListener('click', this.#onDocClick);
    this.#nav.addEventListener('mouseenter', this.#onNavOrMenuEnter);
    this.#nav.addEventListener('mouseleave', this.#onNavLeave);
    this.#menu?.addEventListener('mouseenter', this.#onNavOrMenuEnter);
    this.#menu?.addEventListener('mouseleave', this.#onNavOrMenuLeave);
    this.#backdrop?.addEventListener('click', () => this.#hide());
    this.#mobileToggle?.addEventListener('click', this.#onMobileToggleClick);
    this.#syncTabSettingsFromPanels();
    requestAnimationFrame(() => this.#syncTabSettingsFromPanels());
    // Header hydration can replace tab nodes after connect — re-apply sale chrome.
    window.setTimeout(() => this.#syncTabSettingsFromPanels(), 700);
    window.setTimeout(() => this.#syncTabSettingsFromPanels(), 1800);
    this.#syncStickyMode();
    this.#syncMobileToggleState();
    document.addEventListener('toggle', this.#onDetailsToggleRestoreScrollLock, true);
    this.#bindHeaderDrawerMegaMenu();
    window.addEventListener('resize', this.#onViewportChange, { passive: true });
    window.addEventListener('scroll', this.#onViewportChange, { passive: true });
  }

  #teardown() {
    if (!this.#bound) return;
    this.#bound = false;
    this.removeEventListener('keydown', this.#onKeydown);
    this.removeEventListener('mouseover', this.#onMouseOver);
    this.removeEventListener('click', this.#onTabClick);
    document.removeEventListener('click', this.#onDocClick);
    this.#nav?.removeEventListener('mouseenter', this.#onNavOrMenuEnter);
    this.#nav?.removeEventListener('mouseleave', this.#onNavLeave);
    this.#menu?.removeEventListener('mouseenter', this.#onNavOrMenuEnter);
    this.#menu?.removeEventListener('mouseleave', this.#onNavOrMenuLeave);
    this.#mobileToggle?.removeEventListener('click', this.#onMobileToggleClick);
    window.removeEventListener('scroll', this.#onViewportChange);
    window.removeEventListener('resize', this.#onViewportChange);
    document.removeEventListener('toggle', this.#onDetailsToggleRestoreScrollLock, true);
    this.#headerDrawerDetails?.removeEventListener('toggle', this.#onHeaderDrawerToggleForMega);
    this.#headerDrawerDetails?.classList.remove('dental-mega-replaces-drawer-content');
    this.classList.remove('dental-mega-nav--header-drawer-linked', 'dental-mega-nav--standalone-mobile-toggle');
    this.#maybeRemoveHtmlScrollLock();
  }

  #isMobile() {
    return window.matchMedia('(max-width: 989px)').matches;
  }

  #getTabs() {
    return /** @type {HTMLElement[]} */ ([...this.querySelectorAll('[data-dental-mega-tab]')]);
  }

  #getPanels() {
    return /** @type {HTMLElement[]} */ ([...this.querySelectorAll('.dental-mega-panel')]);
  }

  /** Delegates to `syncDentalMegaTabChrome` for this element only (desktop subtree). */
  #syncTabSettingsFromPanels() {
    syncDentalMegaTabChrome(this);
  }

  /** @param {MouseEvent} e */
  #onMouseOver = (e) => {
    if (this.#isMobile()) return;
    const target = e.target;
    if (!(target instanceof Element)) return;
    const tab = target.closest('[data-dental-mega-tab]');
    if (!(tab instanceof HTMLElement) || !this.contains(tab)) return;
    if (this.#hoverTab === tab) return;
    this.#hoverTab = tab;
    window.clearTimeout(this.#closeTimer);
    window.clearTimeout(this.#openTimer);
    const name = tab.dataset.panel;
    if (!name) return;
    if (!this.#menu?.classList.contains('is-open')) {
      this.#openTimer = window.setTimeout(() => this.#showPanel(name, tab), 40);
    } else {
      this.#showPanel(name, tab);
    }
  };

  /** @param {MouseEvent} e */
  #onTabClick = (e) => {
    const target = e.target;
    if (!(target instanceof Element)) return;
    const tab = target.closest('[data-dental-mega-tab]');
    if (!(tab instanceof HTMLElement) || !this.contains(tab)) return;

    const isLink = tab.tagName === 'A';
    const href = isLink ? tab.getAttribute('href') : '';
    const navigates =
      isLink &&
      href &&
      href !== '#' &&
      href !== '/#' &&
      !href.startsWith('#') &&
      href !== 'http://' &&
      href !== 'https://';

    // Desktop: allow real top-level links to navigate. Mobile drawer handles its own tabs.
    if (navigates && !this.#isMobile()) return;

    e.preventDefault();
    const name = tab.dataset.panel;
    if (!name) return;
    if (normalizeDentalMegaPanelKey(this.#currentPanel) === normalizeDentalMegaPanelKey(name)) {
      this.#hide({ restoreFocus: true });
    } else {
      this.#showPanel(name, tab);
    }
  };

  #onNavLeave = () => {
    this.#hoverTab = null;
    this.#scheduleClose();
  };

  /** Leaving the bar or the flyout starts a short timer before closing (allows diagonal mouse movement). */
  #scheduleClose() {
    this.#closeTimer = window.setTimeout(() => this.#hide(), 220);
  }

  #onNavOrMenuEnter = () => {
    window.clearTimeout(this.#closeTimer);
  };

  #onNavOrMenuLeave = () => {
    this.#hoverTab = null;
    this.#scheduleClose();
  };

  #onViewportChange = () => {
    this.#updateStickyTop();
    this.#updateNavHeight();
    this.#syncMobileToggleState();
    this.#syncHeaderDrawerLinkMode();
    if (this.#menu?.classList.contains('is-open')) this.#updateFlyoutTop();
  };

  #syncMobileToggleState() {
    if (!this.#isMobile()) {
      this.#headerDrawerDetails?.classList.remove('dental-mega-replaces-drawer-content');
      this.classList.remove('is-mobile-open');
      if (this.#headerDrawerDetails?.open) {
        this.#headerDrawerDetails.removeAttribute('open');
      }
    }
    if (this.#mobileToggle) {
      this.#mobileToggle.setAttribute('aria-expanded', this.classList.contains('is-mobile-open') ? 'true' : 'false');
    }
    this.#syncHtmlScrollLock();
  }

  /** Header drawer is visible and usable for the mega menu (not `desktop:hidden` on tablet). */
  #isHeaderDrawerVisible() {
    const container = this.#headerDrawerDetails?.closest('.header__drawer');
    if (!container || !this.#isMobile()) return false;
    const style = window.getComputedStyle(container);
    return style.display !== 'none' && style.visibility !== 'hidden';
  }

  /** Re-bind drawer vs standalone toggle when crossing the 990px breakpoint. */
  #syncHeaderDrawerLinkMode() {
    if (this.classList.contains('dental-mega-nav--in-drawer')) return;
    this.#bindHeaderDrawerMegaMenu();
  }

  /**
   * When the theme drawer is visible, use its hamburger + inline drawer mega.
   * Full-screen overlay only when this bar is visible (legacy); tablet/mobile use drawer markup.
   */
  #bindHeaderDrawerMegaMenu() {
    if (this.classList.contains('dental-mega-nav--in-drawer')) return;
    this.#headerDrawerDetails = document.querySelector('#Details-menu-drawer-container');
    this.#headerDrawerDetails?.removeEventListener('toggle', this.#onHeaderDrawerToggleForMega);
    this.classList.remove('dental-mega-nav--header-drawer-linked', 'dental-mega-nav--standalone-mobile-toggle');
    this.#headerDrawerDetails?.classList.remove('dental-mega-replaces-drawer-content');

    const useDrawerOverlay =
      this.#headerDrawerDetails && this.#canReplaceDrawerOnMobile() && this.#isHeaderDrawerVisible();

    if (!useDrawerOverlay) {
      if (this.#canReplaceDrawerOnMobile()) {
        this.classList.add('dental-mega-nav--standalone-mobile-toggle');
      }
      return;
    }

    this.#headerDrawerDetails.addEventListener('toggle', this.#onHeaderDrawerToggleForMega);
    this.classList.add('dental-mega-nav--header-drawer-linked');
  }

  #canReplaceDrawerOnMobile() {
    /** @type {HTMLElement | null} */
    let node = this;
    while (node && node !== document.documentElement) {
      const style = window.getComputedStyle(node);
      if (style.display === 'none' || style.visibility === 'hidden') return false;
      node = node.parentElement;
    }
    return true;
  }

  #onHeaderDrawerToggleForMega = () => {
    if (!this.#isMobile() || !this.#canReplaceDrawerOnMobile()) return;
    if (this.#headerDrawerDetails?.open) {
      this.classList.add('is-mobile-open');
      this.#headerDrawerDetails.classList.add('dental-mega-replaces-drawer-content');
      this.#ensureMobilePanelOpen();
    } else {
      this.#headerDrawerDetails?.classList.remove('dental-mega-replaces-drawer-content');
      this.classList.remove('is-mobile-open');
      this.#hide();
    }
    if (this.#mobileToggle) {
      this.#mobileToggle.setAttribute('aria-expanded', this.classList.contains('is-mobile-open') ? 'true' : 'false');
    }
    this.#syncHtmlScrollLock();
  };

  #closeMobileOverlay() {
    this.#headerDrawerDetails?.classList.remove('dental-mega-replaces-drawer-content');
    this.classList.remove('is-mobile-open');
    this.#hide({ restoreFocus: false });
    if (this.#headerDrawerDetails?.open) {
      this.#headerDrawerDetails.removeAttribute('open');
    }
    if (this.#mobileToggle) {
      this.#mobileToggle.setAttribute('aria-expanded', 'false');
    }
    const drawerSummary = this.#headerDrawerDetails?.querySelector('summary');
    if (drawerSummary instanceof HTMLElement) {
      drawerSummary.focus();
    } else {
      this.#mobileToggle?.focus();
    }
    this.#syncHtmlScrollLock();
  }

  /** Same as theme `details[scroll-lock]`: lock main page scroll (see assets/base.css + dialog.js). */
  #syncHtmlScrollLock() {
    if (this.#isMobile() && this.classList.contains('is-mobile-open')) {
      document.documentElement.setAttribute('scroll-lock', '');
      return;
    }
    this.#maybeRemoveHtmlScrollLock();
  }

  /** Avoid clearing lock if another drawer (e.g. header menu) is still open. */
  #maybeRemoveHtmlScrollLock() {
    const otherDetailsLockOpen = document.querySelector('details[scroll-lock][open]');
    if (!otherDetailsLockOpen) {
      document.documentElement.removeAttribute('scroll-lock');
    }
  }

  /**
   * If the theme removes `scroll-lock` when another `details[scroll-lock]` closes,
   * re-apply while the mega overlay is still open.
   */
  /** @param {Event} e */
  #onDetailsToggleRestoreScrollLock = (e) => {
    if (!(e.target instanceof HTMLDetailsElement)) return;
    if (!e.target.hasAttribute('scroll-lock')) return;
    requestAnimationFrame(() => {
      if (!this.#isMobile() || !this.classList.contains('is-mobile-open')) return;
      if (!document.documentElement.hasAttribute('scroll-lock')) {
        document.documentElement.setAttribute('scroll-lock', '');
      }
    });
  };

  #onMobileToggleClick = () => {
    if (!this.#isMobile()) return;
    const willOpen = !this.classList.contains('is-mobile-open');
    if (!willOpen) {
      this.#closeMobileOverlay();
      return;
    }
    this.classList.add('is-mobile-open');
    this.#ensureMobilePanelOpen();
    this.#syncMobileToggleState();
  };

  #ensureMobilePanelOpen(force = false) {
    if (!this.#isMobile()) return;
    if (!force && !this.classList.contains('is-mobile-open')) return;
    const tabs = this.#getTabs();
    const anyActive = this.#getPanels().some((panel) => panel.classList.contains('is-active'));
    if (!anyActive && tabs[0]?.dataset.panel) {
      this.#showPanel(tabs[0].dataset.panel, tabs[0]);
    }
  }

  #updateNavHeight() {
    if (!this.#nav) return;
    this.style.setProperty('--dental-mega-nav-height', `${Math.round(this.#nav.getBoundingClientRect().height)}px`);
  }

  #syncStickyMode() {
    this.#stickyEnabled = this.dataset.stickyEnabled === 'true';

    if (this.#stickyEnabled) {
      this.classList.add('is-sticky');
      this.#updateStickyTop();
      this.#updateNavHeight();
      window.addEventListener('scroll', this.#onViewportChange, { passive: true });
      window.addEventListener('resize', this.#onViewportChange, { passive: true });
      return;
    }

    this.classList.remove('is-sticky');
    this.style.removeProperty('--dental-mega-sticky-top');
    this.style.removeProperty('--dental-mega-nav-height');
    window.removeEventListener('scroll', this.#onViewportChange);
    window.removeEventListener('resize', this.#onViewportChange);
  }

  #updateStickyTop() {
    /** @type {HTMLElement | null} */
    const header =
      document.querySelector('header-component') ||
      document.querySelector('store-header') ||
      document.querySelector('[id*="header_section"]');

    if (!header) {
      this.style.setProperty('--dental-mega-sticky-top', '0px');
      return;
    }

    const rect = header.getBoundingClientRect();
    const navHeight = this.#nav ? Math.round(this.#nav.getBoundingClientRect().height) : 0;
    const megaInsideHeader = header.contains(this);
    const offsetBase = megaInsideHeader ? rect.bottom - navHeight : rect.bottom;
    const offset = Math.max(0, Math.round(offsetBase));
    this.style.setProperty('--dental-mega-sticky-top', `${offset}px`);
  }

  /** @param {KeyboardEvent} e */
  #onKeydown = (e) => {
    const tabs = this.#getTabs();
    const activeEl = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const tabIndex = activeEl ? tabs.indexOf(activeEl) : -1;

    if (e.key === 'ArrowRight' || e.key === 'ArrowLeft' || e.key === 'Home' || e.key === 'End') {
      if (tabIndex < 0 || !tabs.length) return;
      e.preventDefault();
      let next = tabIndex;
      if (e.key === 'ArrowRight') next = (tabIndex + 1) % tabs.length;
      if (e.key === 'ArrowLeft') next = (tabIndex - 1 + tabs.length) % tabs.length;
      if (e.key === 'Home') next = 0;
      if (e.key === 'End') next = tabs.length - 1;
      const target = tabs[next];
      target?.focus();
      const name = target?.dataset.panel;
      if (name && !this.#isMobile()) this.#showPanel(name, target);
      return;
    }

    if (e.key === 'Tab' && this.#isMobile() && this.classList.contains('is-mobile-open')) {
      const focusables = this.#getFocusableElements();
      if (!focusables.length) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (e.shiftKey && activeEl === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && activeEl === last) {
        e.preventDefault();
        first.focus();
      }
      return;
    }

    if (e.key !== 'Escape') return;
    if (this.#isMobile() && this.classList.contains('is-mobile-open')) {
      this.#closeMobileOverlay();
      return;
    }
    this.#hide({ restoreFocus: true });
  };

  /** @returns {HTMLElement[]} */
  #getFocusableElements() {
    const selector =
      'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';
    return /** @type {HTMLElement[]} */ (
      [...this.querySelectorAll(selector)].filter((el) => {
        if (!(el instanceof HTMLElement)) return false;
        if (el.closest('[hidden], [aria-hidden="true"]')) return false;
        const style = window.getComputedStyle(el);
        return style.display !== 'none' && style.visibility !== 'hidden';
      })
    );
  }

  /** @param {MouseEvent} e */
  #onDocClick = (e) => {
    const t = /** @type {Node} */ (e.target);
    if (this.contains(t)) return;
    if (this.#isMobile() && this.classList.contains('is-mobile-open')) {
      this.#closeMobileOverlay();
      return;
    }
    this.#hide();
  };

  /**
   * Show one panel: `is-active` on matching `.dental-mega-panel` + tabs; `is-open` on flyout + backdrop.
   * Match by data-panel-handle first (stable), then data-panel id (normalized).
   * @param {string} name Value from `data-panel` on the tab (normalized when compared).
   * @param {HTMLElement | null} [trigger]
   */
  #showPanel(name, trigger = null) {
    // Re-query in case init raced children / morph replaced nodes
    this.#menu = this.querySelector('[data-dental-mega-menu]');
    this.#backdrop = this.querySelector('[data-dental-mega-backdrop]');
    this.#nav = this.querySelector('nav.dental-mega-nav__bar');

    const normalizedName = normalizeDentalMegaPanelKey(name);
    const handle =
      (trigger instanceof HTMLElement && trigger.dataset.panelHandle) ||
      this.#getTabs().find((t) => normalizeDentalMegaPanelKey(t.dataset.panel) === normalizedName)?.dataset
        .panelHandle ||
      '';

    this.#currentPanel = normalizedName;
    if (trigger instanceof HTMLElement) this.#lastTrigger = trigger;

    let anyPanel = false;
    for (const p of this.#getPanels()) {
      const byHandle = handle && p.dataset.panelHandle === handle;
      const byId = normalizeDentalMegaPanelKey(p.dataset.panel) === normalizedName;
      const on = Boolean(byHandle || byId);
      p.classList.toggle('is-active', on);
      if (on) anyPanel = true;
    }

    // If ids diverged and handle missing, still show the first panel so the flyout isn't empty
    if (!anyPanel) {
      const first = this.#getPanels()[0];
      if (first) first.classList.add('is-active');
    }

    for (const tab of this.#getTabs()) {
      const byHandle = handle && tab.dataset.panelHandle === handle;
      const byId = normalizeDentalMegaPanelKey(tab.dataset.panel) === normalizedName;
      const on = Boolean(byHandle || byId);
      tab.classList.toggle('is-active', on);
      tab.setAttribute('aria-expanded', on ? 'true' : 'false');
      if (on) this.#lastTrigger = tab;
    }
    this.#menu?.classList.add('is-open');
    this.#backdrop?.classList.add('is-open');
    this.#updateFlyoutTop();
    if (this.#isMobile() && this.classList.contains('is-mobile-open') && this.#menu) {
      this.#menu.scrollTop = 0;
    }
  }

  #updateFlyoutTop() {
    if (!this.#nav || !this.#menu || this.#isMobile()) return;
    const rect = this.#nav.getBoundingClientRect();
    this.style.setProperty('--dental-mega-flyout-top', `${Math.round(rect.bottom)}px`);
  }

  /**
   * Clear active panel, collapse flyout and backdrop.
   * @param {{ restoreFocus?: boolean }} [opts]
   */
  #hide(opts = {}) {
    const restoreFocus = opts.restoreFocus === true;
    window.clearTimeout(this.#openTimer);
    window.clearTimeout(this.#closeTimer);
    this.#currentPanel = null;
    this.#hoverTab = null;
    for (const p of this.#getPanels()) p.classList.remove('is-active');
    for (const tab of this.#getTabs()) {
      tab.classList.remove('is-active');
      tab.setAttribute('aria-expanded', 'false');
    }
    this.#menu?.classList.remove('is-open');
    this.#backdrop?.classList.remove('is-open');
    if (restoreFocus) this.#lastTrigger?.focus();
  }
}

if (!customElements.get('dental-sale-mega-menu')) {
  customElements.define('dental-sale-mega-menu', DentalSaleMegaMenu);
}

// Drawer mega lives outside the custom element — sync tab chrome after parse, after full load, when the menu opens,
// and when the theme editor replaces a section so sale/chevron classes stay aligned with panel `data-*` attributes.
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    resyncAllDentalMegaTabChrome();
    bindDentalMegaDrawerChromeResync();
    bindDentalMegaChromeMutationObserver();
  });
} else {
  resyncAllDentalMegaTabChrome();
  bindDentalMegaDrawerChromeResync();
  bindDentalMegaChromeMutationObserver();
}
requestAnimationFrame(() => resyncAllDentalMegaTabChrome());

window.addEventListener('load', () => {
  resyncAllDentalMegaTabChrome();
  bindDentalMegaChromeMutationObserver();
  // Header section hydration morphs markup after idle (~0.5–2s). Re-apply sale chrome.
  window.setTimeout(() => resyncAllDentalMegaTabChrome(), 600);
  window.setTimeout(() => resyncAllDentalMegaTabChrome(), 1500);
  window.setTimeout(() => resyncAllDentalMegaTabChrome(), 3000);
});

document.addEventListener('shopify:section:load', (event) => {
  const target = event.target;
  if (target instanceof HTMLElement) {
    target.querySelectorAll('[data-drawer-mega]').forEach((root) => {
      if (root instanceof HTMLElement) syncDentalMegaTabChrome(root);
    });
    target.querySelectorAll('dental-sale-mega-menu').forEach((root) => {
      if (root instanceof HTMLElement) syncDentalMegaTabChrome(root);
    });
  }
  requestAnimationFrame(() => {
    bindDentalMegaDrawerChromeResync();
    bindDentalMegaChromeMutationObserver();
    resyncAllDentalMegaTabChrome();
  });
});
})();
